import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "../../shared/seo/Seo";
import { urls, copy, currentLang } from "../../shared/seo/links";
import useCanonical from "../../shared/seo/useCanonical";
import { useBlogContext } from "../../shared/context/BlogContext";
import BlogMainSection from "../../components/blogs/BlogsMainSection";
import BlogPostCard from "../../components/blogs/BlogsPostCard";
import PageContent from "../../components/blogs/PageContent";
import SideBar from "../../components/blogs/SideBar";
import QuestionPostCard from "../../components/questions/QuestionPostCard";
import Breadcrumbs from "../../components/content/Breadcrumbs";
import FindLawyerBlock from "../../components/content/FindLawyerBlock";
import { breadcrumbJsonLd, contentPath } from "../../components/content/contentLinks";
import ComponentsLoader from "../../components/includes/ComponentsLoader";
import NotFound from "../NotFound/NotFound";
import LoadError from "../../shared/components/LoadError";
import { LOAD_FAILED, LOAD_OK, loadOutcome, loadWithRetry } from "../../shared/helpers/loadResult";
import "../../components/blogs/magazine.css";
import "../../components/content/content.css";

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
}

/**
 * A magazine topic in the page language (/Magazine-Juridique/categorie/<slug>):
 * its published guides and editorial FAQ, the same content the server
 * renderer lists (avoconsulte-be/src/seo/pages.js categoryPage).
 */
export default function Category() {
  const { t } = useTranslation();
  const lang = currentLang();
  const { category: param } = useParams();
  const category = urls.findCategory(safeDecode(param || ""));
  const { getBlogCategory } = useBlogContext();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(!category);
  // A failed request (rate limited, server or network down): not a 404.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!category) {
      setNotFound(true);
      return undefined;
    }
    let live = true;
    setData(null);
    setNotFound(false);
    setFailed(false);
    loadWithRetry(() => getBlogCategory({ category: category.key, language: lang }), () => live).then((res) => {
      if (!live) return;
      const outcome = loadOutcome(res);
      const body = res?.data;
      const guides = body?.guides ?? [];
      const faq = body?.faq ?? [];
      if (outcome === LOAD_FAILED) {
        setFailed(true);
      } else if (outcome !== LOAD_OK || body?.status !== "success" || guides.length + faq.length === 0) {
        // A 404, or a topic with nothing published in this language (the
        // server answers 404 for it too).
        setNotFound(true);
      } else {
        setData({ guides, faq, languages: body.languages ?? [lang], cities: body.cities ?? [] });
      }
    });
    return () => {
      live = false;
    };
  }, [category && category.key, lang, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const canonical = category ? urls.categoryPath(category, lang) : null;
  useCanonical(notFound ? null : canonical);

  if (notFound) return <NotFound />;

  const text = copy.category(lang, category);
  const crumbs = [
    { name: t("Home"), path: urls.homePath(lang) },
    { name: copy.blogs(lang).h1, path: urls.sectionPath("blogs", lang) },
    { name: text.name, path: canonical },
  ];
  const languages = (data && data.languages) || [];
  const alternates =
    languages.length > 1 ? languages.reduce((acc, l) => ({ ...acc, [l]: urls.categoryPath(category, l) }), {}) : null;
  const items = data ? [...data.guides, ...data.faq] : [];

  // Until the topic is in, the server-rendered head (hreflang lists the
  // languages that have content) stays: no <Seo> to replace it.
  return (
    <>
      {data && (
        <Seo
          title={text.title}
          description={text.description}
          path={canonical}
          alternates={alternates}
          jsonLd={[
            breadcrumbJsonLd(crumbs),
            ...(items.length
              ? [
                  {
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    name: text.name,
                    numberOfItems: items.length,
                    itemListElement: items.map((item, i) => ({
                      "@type": "ListItem",
                      position: i + 1,
                      url: urls.absoluteUrl(window.location.origin, contentPath(item)),
                    })),
                  },
                ]
              : []),
          ]}
        />
      )}
      <section className="avc-page-hero">
        <div className="container avc-page-hero__inner">
          <Breadcrumbs items={crumbs} tone="dark" />
          <span className="avc-eyebrow">
            <i className="fas fa-tag" aria-hidden="true"></i>
            {t("topic_label")}
          </span>
          <h1 className="avc-page-hero__title">{text.h1}</h1>
          <p className="avc-page-hero__lead">{text.intro}</p>
        </div>
      </section>
      <PageContent>
        <BlogMainSection>
          {failed ? (
            <div className="col-12">
              <LoadError onRetry={() => setAttempt((n) => n + 1)} />
            </div>
          ) : !data ? (
            <ComponentsLoader page />
          ) : (
            <div className="col-12">
              {data.guides.length > 0 && (
                <section className="avc-topic-section" aria-labelledby="avc-topic-guides">
                  <h2 className="avc-topic-section__title" id="avc-topic-guides">
                    {t("topic_guides")}
                  </h2>
                  <div className="row">
                    {data.guides.map((guide) => (
                      <BlogPostCard key={guide.id} blog={guide} />
                    ))}
                  </div>
                </section>
              )}
              {data.faq.length > 0 && (
                <section className="avc-topic-section" aria-labelledby="avc-topic-faq">
                  <h2 className="avc-topic-section__title" id="avc-topic-faq">
                    {t("topic_faq")}
                  </h2>
                  {data.faq.map((question) => (
                    <QuestionPostCard key={question.id} question={{ ...question, content: question.description, editorial: true }} />
                  ))}
                </section>
              )}
              <FindLawyerBlock cities={data.cities} />
            </div>
          )}
        </BlogMainSection>
        <SideBar />
      </PageContent>
    </>
  );
}
